import { can, type GarageRole, inviteInput, MEMBER_ROLES, type MemberRole } from "@bt/domain";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  BanIcon,
  CheckCircle2Icon,
  ClockAlertIcon,
  MailIcon,
  MoreHorizontalIcon,
  PauseCircleIcon,
  UserPlusIcon,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Avatar } from "@/components/app/avatar";
import { EmptyState } from "@/components/app/empty-state";
import { Field } from "@/components/app/field";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Segmented } from "@/components/ui/misc";
import { displayName } from "@/lib/account";
import { errorMessage } from "@/lib/errors";
import { useFormat } from "@/lib/format";
import { useGarageLanguage } from "@/lib/preferences";
import { type RouterOutputs, useTRPC } from "@/lib/trpc";

type MemberList = RouterOutputs["members"]["list"];
type Member = MemberList["members"][number];
type Invitation = MemberList["invitations"][number];

/**
 * A garage's members and open invitations (ADR 0011). Everyone in the garage sees the members;
 * garage admins (and platform admins) invite, change roles, deactivate and remove. The tRPC
 * provider around it decides which garage: the current one, or the admin's choice.
 */
export function Members({ garageName }: { garageName: string }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const { data } = useQuery(trpc.members.list.queryOptions());
  const { data: access } = useQuery(trpc.garages.access.queryOptions());
  const manage = access ? can(access, "members.manage") : false;
  const [inviting, setInviting] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      {manage ? (
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setInviting(true)}>
            <UserPlusIcon />
            {t("members.invite")}
          </Button>
        </div>
      ) : null}
      {data && !data.members.length && !data.invitations.length ? (
        <EmptyState compact icon={UserPlusIcon} title={t("members.empty")} />
      ) : null}
      {data?.members.length ? (
        <Card className="divide-y divide-border">
          {data.members.map((member) => (
            <MemberRow
              key={member.userId}
              member={member}
              isMe={member.userId === data.me}
              manage={manage}
              garageName={garageName}
            />
          ))}
        </Card>
      ) : null}
      {data?.invitations.length ? (
        <>
          <h3 className="mt-2 text-sm font-semibold tracking-tight">{t("members.invitations")}</h3>
          <Card className="divide-y divide-border">
            {data.invitations.map((invitation) => (
              <InvitationRow key={invitation.id} invitation={invitation} manage={manage} />
            ))}
          </Card>
        </>
      ) : null}
      <InviteDialog open={inviting} onOpenChange={setInviting} />
    </div>
  );
}

function MemberStatusBadge({ member }: { member: Member }) {
  const { t } = useTranslation();
  if (member.userStatus !== "active") {
    return (
      <Badge tone="missing">
        <BanIcon />
        {t("members.accountDisabled")}
      </Badge>
    );
  }
  return member.status === "active" ? (
    <Badge tone="ok">
      <CheckCircle2Icon />
      {t("memberStatus.active")}
    </Badge>
  ) : (
    <Badge tone="neutral">
      <PauseCircleIcon />
      {t("memberStatus.deactivated")}
    </Badge>
  );
}

function MemberRow({
  member,
  isMe,
  manage,
  garageName,
}: {
  member: Member;
  isMe: boolean;
  manage: boolean;
  garageName: string;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useFormat();
  const [removing, setRemoving] = useState(false);
  const changeRole = useMutation(trpc.members.changeRole.mutationOptions());
  const deactivate = useMutation(trpc.members.deactivate.mutationOptions());
  const reactivate = useMutation(trpc.members.reactivate.mutationOptions());
  const remove = useMutation(trpc.members.remove.mutationOptions());
  const name = displayName(member);
  const otherRole: GarageRole = member.role === "garage_admin" ? "garage_user" : "garage_admin";

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <Avatar user={member} className="mt-0.5 size-9" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {name}
          {isMe ? (
            <span className="font-normal text-muted-foreground"> ({t("members.you")})</span>
          ) : null}
        </p>
        <p className="truncate text-xs text-muted-foreground">{member.email}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {member.lastLoginAt
            ? t("members.lastSignIn", { when: format.relativeTime(member.lastLoginAt) })
            : t("members.neverSignedIn")}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <Badge tone={member.role === "garage_admin" ? "brand" : "outline"}>
            {t(`roles.${member.role}`)}
          </Badge>
          <MemberStatusBadge member={member} />
        </div>
      </div>
      {manage ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={t("members.actions", { name })}>
              <MoreHorizontalIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={() => changeRole.mutate({ userId: member.userId, role: otherRole })}
            >
              {otherRole === "garage_admin"
                ? t("members.makeGarageAdmin")
                : t("members.makeGarageUser")}
            </DropdownMenuItem>
            {member.status === "active" ? (
              <DropdownMenuItem onSelect={() => deactivate.mutate({ userId: member.userId })}>
                {t("members.deactivate")}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={() => reactivate.mutate({ userId: member.userId })}>
                {t("members.reactivate")}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onSelect={() => setRemoving(true)}
            >
              {t("members.remove")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <ConfirmDialog
        open={removing}
        onOpenChange={setRemoving}
        title={t("members.removeTitle", { name, garage: garageName })}
        description={t("members.removeBody")}
        confirmLabel={t("common.remove")}
        destructive
        pending={remove.isPending}
        onConfirm={() =>
          remove.mutate({ userId: member.userId }, { onSettled: () => setRemoving(false) })
        }
      />
    </div>
  );
}

function InvitationRow({ invitation, manage }: { invitation: Invitation; manage: boolean }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useFormat();
  const resend = useMutation(trpc.members.resend.mutationOptions());
  const revoke = useMutation(trpc.members.revoke.mutationOptions());
  const name = displayName(invitation);
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <Avatar user={{ ...invitation, avatarUrl: null }} className="mt-0.5 size-9 opacity-70" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{name}</p>
        <p className="truncate text-xs text-muted-foreground">{invitation.email}</p>
        {invitation.invitedBy ? (
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t("members.invitedBy", { name: invitation.invitedBy })}
          </p>
        ) : null}
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {invitation.role ? <Badge tone="outline">{t(`roles.${invitation.role}`)}</Badge> : null}
          {invitation.state === "pending" ? (
            <Badge tone="transit">
              <MailIcon />
              {t("memberStatus.invited", { date: format.date(invitation.expiresAt) })}
            </Badge>
          ) : (
            <Badge tone="partial">
              <ClockAlertIcon />
              {t("memberStatus.expired")}
            </Badge>
          )}
        </div>
      </div>
      {manage ? (
        <div className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center">
          <Button
            variant="outline"
            size="sm"
            disabled={resend.isPending}
            onClick={() =>
              resend.mutate(
                { id: invitation.id },
                { onSuccess: () => toast.success(t("members.resent")) },
              )
            }
          >
            {t("members.resend")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={revoke.isPending}
            onClick={() =>
              revoke.mutate(
                { id: invitation.id },
                { onSuccess: () => toast.success(t("members.revoked")) },
              )
            }
          >
            {t("members.revoke")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Email, name and role of someone to invite. */
export function RoleChoice({
  value,
  onChange,
  label,
}: {
  value: MemberRole;
  onChange: (role: MemberRole) => void;
  label: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1.5">
      <Segmented<MemberRole>
        aria-label={label}
        value={value}
        onChange={onChange}
        options={MEMBER_ROLES.map((role) => ({ value: role, label: t(`roles.${role}`) }))}
      />
      <p className="text-xs text-muted-foreground">{t(`roleHelp.${value}`)}</p>
    </div>
  );
}

function InviteDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader title={t("members.inviteTitle")} description={t("members.inviteBody")} />
        {open ? <InviteForm onDone={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function InviteForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const garageLanguage = useGarageLanguage();
  const trpc = useTRPC();
  const invite = useMutation({ ...trpc.members.invite.mutationOptions(), meta: { silent: true } });
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState<MemberRole>("garage_user");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  return (
    <form
      className="contents"
      onSubmit={async (event) => {
        event.preventDefault();
        const parsed = inviteInput.safeParse({ email, firstName, lastName, role });
        if (!parsed.success) {
          setErrors(
            Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])),
          );
          return;
        }
        setErrors({});
        try {
          await invite.mutateAsync({ ...parsed.data, language: garageLanguage });
          toast.success(t("members.invited", { email: parsed.data.email }));
          onDone();
        } catch (error) {
          setServerError(errorMessage(error, t));
        }
      }}
    >
      <DialogBody className="flex flex-col gap-4">
        <Field label={t("account.email")} htmlFor="invite-email" error={errors.email}>
          <Input
            id="invite-email"
            autoFocus
            inputMode="email"
            autoComplete="off"
            value={email}
            aria-invalid={errors.email ? true : undefined}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("account.firstName")} htmlFor="invite-first" error={errors.firstName}>
            <Input
              id="invite-first"
              autoComplete="off"
              value={firstName}
              aria-invalid={errors.firstName ? true : undefined}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Field>
          <Field label={t("account.lastName")} htmlFor="invite-last" error={errors.lastName}>
            <Input
              id="invite-last"
              autoComplete="off"
              value={lastName}
              aria-invalid={errors.lastName ? true : undefined}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Field>
        </div>
        <Field label={t("members.role")}>
          <RoleChoice value={role} onChange={setRole} label={t("members.role")} />
        </Field>
        {serverError ? (
          <p className="text-sm text-destructive" role="alert">
            {serverError}
          </p>
        ) : null}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onDone}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={invite.isPending}>
          {t("members.send")}
        </Button>
      </DialogFooter>
    </form>
  );
}
